import { useQuery } from "@apollo/client";
import { useImmer } from "use-immer";
import { compareObjectVals } from "crud-object-diff";
import { useState } from "react";

import { FEE_EARNER_LEVELS_AND_FEE_EARNERS_QUERY } from "../graphql/query";
import {
  FeeEarnerLevelsAndFeeEarners,
  FeeEarnerLevelsAndFeeEarners_feeEarnerLevels_records,
} from "../graphql/__generated_types__/FeeEarnerLevelsAndFeeEarners";
import { FeeEarnerLevel } from "@design-system";

export interface AllFeeEarnerLevelsAndFeeEarnersOption<IsFeeEarner extends boolean> {
  value: string | number;
  label: string;
  disabled?: boolean;
  children?: IsFeeEarner extends false ? AllFeeEarnerLevelsAndFeeEarnersOption<true>[] : undefined;
  loading?: boolean;
  isLeaf?: IsFeeEarner extends false ? false : boolean;
  isFeeEarnerLevelRate?: boolean;
  feeEarnerLevel?: IsFeeEarner extends true ? number : undefined;
}
export const useAllFeeEarnerLevelsAndFeeEarnersQuery = (
  feeEarnersAreLeafs = false,
  showFeeEarnerLevelAsFeeEarner: boolean
) => {
  const [feeEarnersAndLevelsOptions, setFeeEarnersAndLevelsOptions] = useImmer<
    AllFeeEarnerLevelsAndFeeEarnersOption<boolean>[]
  >([]);
  const [allLevels, setAllLevels] = useState<FeeEarnerLevel[]>([]);
  // const [allLevels, setAllLevels] = useState<FeeEarnerLevel[]>([]);
  const { loading } = useQuery<FeeEarnerLevelsAndFeeEarners>(
    FEE_EARNER_LEVELS_AND_FEE_EARNERS_QUERY,
    {
      onCompleted: (data) => {
        setAllLevels(data.feeEarnerLevels.records);
        const feeEarnerLevelsData = data.feeEarnerLevels.records.map(
          (feeEarnerLevel: FeeEarnerLevelsAndFeeEarners_feeEarnerLevels_records) => {
            const children = feeEarnerLevel.feeEarners.map((fe: any) => {
              return {
                label: fe.name,
                value: fe.id,
                isLeaf: feeEarnersAreLeafs,
                feeEarnerLevel: feeEarnerLevel.id || 0,
                isFeeEarnerLevelRate: false,
              };
            });
            if (showFeeEarnerLevelAsFeeEarner) {
              children.unshift({
                isFeeEarnerLevelRate: true,
                feeEarnerLevel: feeEarnerLevel.id || 0,
                label: `${feeEarnerLevel.description} Rates   👤 `,
                value: `level-rate-${feeEarnerLevel.id.toString()}`,
                isLeaf: feeEarnersAreLeafs,
              });
            }
            return {
              label: feeEarnerLevel.description,
              value: feeEarnerLevel.id,
              isLeaf: false,
              children,
            } as AllFeeEarnerLevelsAndFeeEarnersOption<false>;
          }
        );

        setFeeEarnersAndLevelsOptions((draft) => {
          const feDataDiff = compareObjectVals([draft, feeEarnerLevelsData]);
          // If the incoming data (feeEarnerLevelsData) differs from the existing data (draft)...
          // ...use it – This is shown via the 'createdVals' prop
          if (feDataDiff.createdVals) {
            return feeEarnerLevelsData;
          }
        });
      },
    }
  );

  return {
    feeEarnerLevelsAndFeeEarners: feeEarnersAndLevelsOptions,
    setFeeEarnerLevelsAndFeeEarners: setFeeEarnersAndLevelsOptions,
    allLevels,
    loading,
  };
};
